<template>
  <div class="location-play-controller">
    <!-- 減速ボタン -->
    <icon-rewind
      class="control-icon"
      v-bind:class="{ disabled: speedIndex === 0 }"
      v-on:click="$_speedDown"
    />
    <!-- 再生/停止ボタン -->
    <template>
      <icon-pause v-if="isPlaying" class="control-icon" v-on:click="stop" />
      <icon-play v-else class="control-icon" v-on:click="play" />
    </template>
    <!-- 加速ボタン -->
    <icon-fast-forward
      class="control-icon"
      v-bind:class="{ disabled: speedIndex === speeds.length - 1 }"
      v-on:click="$_speedUp"
    />
    <!-- 再生速度 -->
    <div class="speed-indicator">{{ speedText }}</div>
    <!-- スライダー -->
    <div class="time-slider">
      <vue-slider
        v-model="currentTime"
        v-bind="sliderProps"
        v-on:drag-end="$_emitCurrentTimeChangedEvent"
      />
    </div>
    <!-- 現在時刻 -->
    <div class="current-time">{{ currentTimeText }}</div>
  </div>
</template>

<script>
import moment from 'moment'
import IconPlay from '@/assets/ifield/ic_play.svg?component'
import IconPause from '@/assets/ifield/ic_pause.svg?component'
import IconFastForward from '@/assets/ifield/ic_fast-forward.svg?component'
import IconRewind from '@/assets/ifield/ic_rewind.svg?component'
import VueSlider from 'vue-slider-component'
import { DATE_FORMAT_HYPHEN, TIME_FORMAT_SECOND } from '@/constants.js'
import VARIABLES from '@/assets/scss/variables.module.scss'

// 日時フォーマット
const DATETIME_FORMAT = `${DATE_FORMAT_HYPHEN} ${TIME_FORMAT_SECOND}`

/**
 * 差分取得用現在時間取得
 * http://yomotsu.net/blog/2013/01/05/fps.html
 * @returns {Number}
 */
const getTime = () => {
  return performance.now() || new Date().getTime()
}

export default {
  name: 'AppLocationPlayController',
  components: {
    'icon-play': IconPlay,
    'icon-pause': IconPause,
    'icon-fast-forward': IconFastForward,
    'icon-rewind': IconRewind,
    'vue-slider': VueSlider,
  },
  props: {
    /**
     * 開始日時
     */
    from: {
      type: String,
      default: null,
    },
    /**
     * 終了日時
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * 再生速度選択肢
     */
    speeds: {
      type: Array,
      default: () => [1, 2, 5, 10, 20, 50, 100, 200],
    },
  },
  data() {
    return {
      // 現在時間
      currentTime: +moment(this.from),
      // 再生速度インデックス
      speedIndex: 0,
      // 再生中フラグ
      isPlaying: false,
      // 前回時間(差分用)
      lastTime: 0,
    }
  },
  computed: {
    /**
     * 時間範囲
     * @type {Object}
     */
    timeRange: function () {
      return {
        min: +moment(this.from),
        max: +moment(this.to),
      }
    },
    /**
     * 再生速度
     * @type {Number}
     */
    speed: function () {
      return this.speeds[this.speedIndex]
    },
    /**
     * 速度テキスト
     * @type {String}
     */
    speedText: function () {
      return `×${this.speed}`
    },
    /**
     * 再生速度がmaxの場合はtrue
     * @type {Boolean}
     */
    /**
     * 現在時間テキスト
     * @type {String}
     */
    currentTimeText: function () {
      return moment(this.currentTime).format(DATETIME_FORMAT)
    },
    /**
     * スライダー用props
     * @type {Object}
     */
    sliderProps: function () {
      return {
        min: this.timeRange.min,
        max: this.timeRange.max,
        intereval: 60,
        duration: 0,
        adsorb: true,
        contained: true,
        // tooltip: 'always',
        tooltipPlacement: 'top',
        width: '100%',
        height: '0.5rem',
        dotSize: 16,
        direction: 'ltr',
        tooltipFormatter: (v) => moment(v).format('HH:mm:ss'),
        order: false,
        useKeyboard: false,
        dragOnClick: true,
        silent: process.env.NODE_ENV === 'production',
        dotStyle: {
          backgroundColor: VARIABLES.ENTITY_COLOR_GREEN,
          border: `1px solid ${VARIABLES.ENTITY_COLOR_GREEN}`,
        },
        railStyle: {
          backgroundColor: VARIABLES.ENTITY_COLOR_WHITE,
          boxShadow: 'none',
        },
        processStyle: {
          backgroundColor: VARIABLES.ENTITY_COLOR_GREEN,
          boxShadow: 'none',
          left: '-1px',
        },
        tooltipStyle: {
          backgroundColor: VARIABLES.ENTITY_COLOR_WHITE,
          boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.24)',
          fontFamily: 'monospace',
          fontSize: '0.875rem',
        },
      }
    },
  },
  mounted: function () {
    /**
     * マウント完了イベント
     * @param {VueComponent} 自身のインスタンス
     */
    this.$emit('mounted', this)
  },
  beforeDestroy: function () {
    this.stop()
  },
  methods: {
    /**
     * 再生
     * @public
     */
    play: function () {
      this.isPlaying = true
      this.lastTime = getTime()
      this.$_playLoop()
    },

    /**
     * 停止
     * @public
     */
    stop: function () {
      this.isPlaying = false
    },

    /**
     * 加速
     */
    $_speedUp: function () {
      if (this.speedIndex < this.speeds.length - 1) {
        this.speedIndex += 1
      }
    },

    /**
     * 減速
     */
    $_speedDown: function () {
      if (this.speedIndex > 0) {
        this.speedIndex -= 1
      }
    },

    /**
     * 再生処理
     */
    $_playLoop: function () {
      if (this.isPlaying) {
        if (this.currentTime > this.timeRange.max) {
          this.currentTime = this.timeRange.max
          this.stop()
          return
        }
        const time = getTime()
        const delta = time - this.lastTime
        this.currentTime += delta * this.speed
        this.lastTime = time
        this.$_emitCurrentTimeChangedEvent()
        requestAnimationFrame(this.$_playLoop)
      }
    },

    /**
     * 現在時刻変更イベント発行
     */
    $_emitCurrentTimeChangedEvent: function () {
      /**
       * 現在時刻変更イベント
       * @param {Number} currentTime 現在時刻(unixtime)
       */
      this.$emit('current-time-changed', this.currentTime)
    },
  },
}
</script>

<style lang="scss" scoped>
.location-play-controller {
  height: 48px;
  min-width: 550px;
  padding: 8px 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  user-select: none;
  column-gap: 16px;
  background-color: rgba(120, 120, 120, 0.9);
  border-radius: 4px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.24);
  color: #fff;
}

.control-icon {
  width: 24px;
  height: 24px;
  fill: $entity-color-white;
  cursor: pointer;

  &.disabled {
    fill: $entity-color-light-gray;
  }
}

.time-slider {
  flex-grow: 1;
  min-width: 200px;
}

.speed-indicator {
  width: 2.2rem;
  font-size: 0.75rem;
  text-align: center;
  margin-left: -8px;
}

.current-time {
  width: 10rem;
  font-size: 0.875rem;
  text-align: right;
  font-family: monospace;
}
</style>
